<script setup lang="ts">
/**
 * Over your plan's people (until it fits), and an organization whose access was
 * paused (once per pause, until dismissed).
 */
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import DAlert from '../../design/components/DAlert.vue';
import DButton from '../../design/components/DButton.vue';
import { useSessionStore } from '../../app/stores/session.ts';

const session = useSessionStore();
const route = useRoute();
const SEEN_KEY = 'dometry:pause-seen';

const seen = ref<string[]>(readSeen());
const pauseKey = (o: { id: string; pause: { since: string } | null }) =>
  `${o.id}:${o.pause?.since}`;
// Not on that org's own paused page (it already says so).
const newlyPaused = computed(() =>
  session.orgs.filter(
    (o) =>
      o.pause !== null &&
      !seen.value.includes(pauseKey(o)) &&
      !(route.name === 'org-paused' && route.params['org'] === o.slug),
  ),
);
const graceDate = computed(() => {
  const until = session.planNotice?.graceUntil;
  return until ? new Date(until).toLocaleDateString() : null;
});

function dismiss(o: { id: string; pause: { since: string } | null }) {
  // Only pauses still in place are kept, so the list never grows.
  const live = session.orgs.filter((x) => x.pause !== null).map(pauseKey);
  seen.value = [...seen.value.filter((k) => live.includes(k)), pauseKey(o)];
  try {
    globalThis.localStorage?.setItem(SEEN_KEY, JSON.stringify(seen.value));
  } catch {
    // Storage blocked: it shows again next time.
  }
}

function readSeen(): string[] {
  try {
    const parsed: unknown = JSON.parse(globalThis.localStorage?.getItem(SEEN_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}
</script>

<template>
  <DAlert v-if="session.planNotice" tone="error">
    <div class="account-banner">
      <span class="account-banner__text">
        Your plan changed: you have {{ session.planNotice.people }} people, and it allows
        {{ session.planNotice.allowed }}.
        <template v-if="graceDate"
          >Choose who keeps access by {{ graceDate }}, or the people who joined most recently are
          paused then.</template
        >
      </span>
      <RouterLink :to="{ name: 'plan' }" class="button button--subtle button--sm"
        >Choose who keeps access</RouterLink
      >
    </div>
  </DAlert>
  <DAlert v-for="o in newlyPaused" :key="pauseKey(o)">
    <div class="account-banner">
      <span class="account-banner__text">Your access to {{ o.name }} was paused.</span>
      <RouterLink
        :to="{ name: 'org-paused', params: { org: o.slug } }"
        class="button button--subtle button--sm"
        @click="dismiss(o)"
        >See why</RouterLink
      >
      <DButton variant="ghost" size="sm" @click="dismiss(o)">Dismiss</DButton>
    </div>
  </DAlert>
</template>
